// 活动详情「活动规则」展示纯函数。
// 规格来源：jielong_small/doc/handover/2026-08-18-活动规则交接-solataireGroupDetails.md；
// 与小程序侧 BmRules.vue / solataireGroupDetails.vue computed formattedDays 对齐。
// 注意：本文件被 test/activityRulesDisplay.test.mjs 以 data: URL 按 ESM 加载，不能有 import 依赖。

// 后端约定的永久有效哨兵值
const LONG_TERM_END_TIME = "2099-12-31 23:59:59";

/**
 * 去掉时间的秒部分，如 "2026-12-12 12:12:30" -> "2026-12-12 12:12"。
 * 仅在确有秒位（HH:mm:ss 结尾）时去秒，分钟精度输入原样返回。
 * （小程序 BmRules 用 /:\d{2}$/，会把分钟精度 ":mm" 误删，这里收紧。）
 */
export function removeSeconds(timeStr) {
  if (!timeStr) return "";
  return String(timeStr).replace(/(\d{2}:\d{2}):\d{2}$/, "$1");
}

/**
 * 报名时间/活动时间范围中「结束时间」的展示文案：
 * 永久有效哨兵值 -> 「永久有效」；其他 -> 去秒；空值 -> 空串。
 */
export function formatRangeEndTime(endTime) {
  if (!endTime) return "";
  if (endTime === LONG_TERM_END_TIME) return "永久有效";
  return removeSeconds(endTime);
}

/**
 * 报名截止时间的展示文案。永久有效有两套判断（交接文档 §9.7）：
 * 字符串哨兵值 或 年份为 2099，这里收口为一个方法，别再写第三套。
 * 普通时间原样返回，调用方需要去秒时组合 removeSeconds。
 */
export function formatEndTime(endTime) {
  if (!endTime) return "";
  if (endTime === LONG_TERM_END_TIME) return "永久有效";
  // "2026-08-18 12:00:00" 中的 - 在部分浏览器（Safari）无法被 Date 解析，先替换为 /
  const date = new Date(String(endTime).replace(/-/g, "/"));
  if (isNaN(date.getTime())) return endTime;
  return date.getFullYear() === 2099 ? "永久有效" : endTime;
}

/**
 * 解析参与地点 activityDetail.deliveryaddress（"lat,lng" 字符串）。
 * 非两段格式统一返回空坐标。
 */
export function formatLatLng(address) {
  if (!address) return { lat: "", lng: "" };
  const arr = String(address).split(",");
  if (arr.length === 2) {
    return { lat: arr[0], lng: arr[1] };
  }
  return { lat: "", lng: "" };
}

/**
 * 每周多天打卡 clockInfo.clockFrequency（逗号分隔 0-6，0=周一）映射为「周一~周日」文案。
 */
export function formatClockFrequencyDays(clockFrequency) {
  if (!clockFrequency) return "";
  const daysMap = ["周一", "周二", "周三", "周四", "周五", "周六", "周日"];
  const days = String(clockFrequency)
    .split(",")
    .map((num) => Number(num.trim()))
    .filter((num) => num >= 0 && num <= 6);
  return days.map((num) => daysMap[num]).join(", ");
}

/**
 * 统计行（参与/分享/浏览）的展示数字：
 * 开启加密浏览（encryptBrowsing==1）且当前用户是普通用户（isOtherPeople==3）→ 脱敏「***」；
 * 否则显示真实值，空值（0/null/undefined）兜底显示 0。
 */
export function formatStatCount(count, encryptBrowsing, isOtherPeople) {
  if (encryptBrowsing == 1 && isOtherPeople == 3) return "***";
  return count ? count : 0;
}

/**
 * 签退在场时间 attendanceDuration 的展示文案（单位分钟）：
 * null/0/空 → 「不限制」（后端以 null 表示不限制，0 同义，与创建/编辑侧档位一致）；
 * 正数（预设档位或自定义输入的分钟数）→ 「N分钟」；非数字字符串兜底「不限制」。
 */
export function formatAttendanceDuration(duration) {
  const val = Number(duration);
  if (!val) return "不限制";
  return val + "分钟";
}
